import { BehaviorSubject, combineLatest, EMPTY, Observable, of, timer } from 'rxjs';
import { delayWhen, filter, map, skip } from 'rxjs/operators';
import { BreakpointMinService } from 'src/app/@utils/breakpoint-min.service';
import { isDefined } from 'src/app/@utils/is-defined';

import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, input, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FilterNavigationService } from '@core/services/filter/filter-navigation.service';
import { FilterService } from '@core/services/filter/filter.service';
import { LoggingService } from '@core/services/logging.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { SchemaService } from '@core/services/schema.service';
import { SettingsService } from '@core/services/settings.service';
import { MobileFilterComponent } from '@features/product-filter/mobile-filter/mobile-filter.component';
import { errorMessages, FilterType } from '@features/product-filter/product-filter.config';
import { Properties } from '@features/product-filter/product-filter/product-filter.models';
import { OverlayService } from '@libs/overlay';
import { ProductSearchViewModel } from '@models/api-search.models';
import { Schema } from '@models/structured-data.models';
import { BreadCrumb } from '@models/umbraco.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { scrollContainer } from '@shared/scroll/scroll-container';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { SameDayDeliveryParams } from '@features/product-filter/same-day-delivery-facet/same-day-delivery-facet.model';
import { GetUspCardPipe } from '../pipes/get-usp-card.pipe';
import { GetSearchParamPipe } from '../pipes/get-search-param.pipe';
import { ToFacetExposedCollectionPipe } from '@features/product-filter/pipes/to-facet-exposed-collection.pipe';
import { ExistsAndGreaterThanZeroPipe } from '@shared/number-pipes/exists-and-greater-than-zero.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { FormatStringPipe } from '@shared/format-string/format-string.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { LoadMoreComponent } from '../load-more/load-more.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { ProductUspCardComponent } from '../../product/product-usp-card/product-usp-card.component';
import { ProductCardComponent } from '../../product/product-card/product-card.component';
import { ImpressionTrackingDirective } from '@shared/tracking/impression-tracking/impression-tracking.directive';
import { FilterCountComponent } from '../filter-count/filter-count.component';
import { SortingComponent } from '../sorting/sorting.component';
import { SameDayDeliveryFacetComponent } from '../same-day-delivery-facet/same-day-delivery-facet.component';
import { StoreStockFacetComponent } from '../store-stock-facet/store-stock-facet.component';
import { ResultCountComponent } from '../result-count/result-count.component';
import { FacetTagsComponent } from '../facet-tags/facet-tags.component';
import { FilterFacetsComponent } from '../filter-facets/filter-facets.component';
import { CategoryNavigationComponent } from '../category-navigation/category-navigation.component';
import { AsyncPipe, NgClass, NgFor, NgIf, NgStyle } from '@angular/common';
import { StructuredDataComponent } from '@shared/seo/structured-data/structured-data.component';
import { NgAfterViewInitDirective } from '@shared/ng-after-view-init/ng-after-view-init.directive';
import { PromotionImpressionTrackingDirective } from '@shared/tracking/impression-tracking/promotion-impression-tracking.directive';
import { buildSearchParams } from '@features/product-filter/helpers/build-search-params.helper';
import {
    RelewiseRetailMediaParams,
    RelewiseRetailMediaVariation
} from '@core/services/relewise/relewise-tracking.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { mapToRelewiseVariation } from '@core/services/relewise/map-to-relewise-variation.helper';

@UntilDestroy()
@Component({
    selector: 'app-product-filter',
    templateUrl: './product-filter.component.html',
    styleUrls: ['./product-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        StructuredDataComponent,
        NgClass,
        NgIf,
        CategoryNavigationComponent,
        NgStyle,
        FilterFacetsComponent,
        FacetTagsComponent,
        ResultCountComponent,
        StoreStockFacetComponent,
        SameDayDeliveryFacetComponent,
        SortingComponent,
        FilterCountComponent,
        NgFor,
        ImpressionTrackingDirective,
        ProductCardComponent,
        ProductUspCardComponent,
        SvgIconComponent,
        LoadMoreComponent,
        LoadingDotsComponent,
        AsyncPipe,
        SafePipe,
        FormatStringPipe,
        TranslateSafePipe,
        ExistsAndGreaterThanZeroPipe,
        ToFacetExposedCollectionPipe,
        GetSearchParamPipe,
        GetUspCardPipe,
        PromotionImpressionTrackingDirective,
        NgAfterViewInitDirective
    ]
})
export class ProductFilterComponent implements OnInit {
    private readonly isPendingDelayedBs$ = new BehaviorSubject<boolean>(false);
    private readonly breakpoints = toSignal(this.breakpointMinService.breakpoint$.pipe(untilDestroyed(this)));

    public isPendingDelayed$ = this.isPendingDelayedBs$.asObservable();
    public filterTypes = FilterType;
    public isLoadMore: boolean = false;
    public scrollContainer = scrollContainer;

    public storeStockDisabled: boolean;
    public filterStickyOffset$: Observable<number> = this.scrollService.hideHeader$.pipe(
        map(hideHeader => (hideHeader ? 0 : this.scrollService.getHeaderHeight())),
        untilDestroyed(this)
    );
    public itemListSchema: Schema;

    public readonly isDesktopOrLarger = computed(() => {
        this.setRelewiseParams(mapToRelewiseVariation(this.breakpoints()));
        return mapToRelewiseVariation(this.breakpoints()) === 'DESKTOP';
    });

    public readonly filterType = input<FilterType>(FilterType.CategoryResult);

    @Input()
    public readonly title?: string;

    @Input()
    public readonly filterPageId?: string;

    @Input()
    public readonly rootId?: string;

    @Input()
    public readonly searchTerm?: string;

    @Input()
    public readonly pageBreadCrumbs?: BreadCrumb[];

    @Input()
    public readonly properties?: Properties;

    constructor(
        private readonly loggingService: LoggingService,
        private readonly notificationService: NotificationService,
        private readonly overlayService: OverlayService,
        private readonly cd: ChangeDetectorRef,
        private readonly schemaService: SchemaService,
        private readonly settingsService: SettingsService,
        public route: ActivatedRoute,
        public filterService: FilterService,
        public filterNavigationService: FilterNavigationService,
        public scrollService: ScrollService,
        public breakpointMinService: BreakpointMinService
    ) {
        combineLatest([this.filterService.filterList$, this.settingsService.settings$])
            .pipe(untilDestroyed(this))
            .subscribe(([list, settings]) => {
                if (list?.length) {
                    this.itemListSchema = this.schemaService.getListProductSchema(
                        list,
                        settings.ecommerceSettings?.currencyCode
                    );
                }
            });

        // For showing pending states with a forced delay
        // to make sure the user does not experience "flashes" of loading indicators
        this.filterService.pending$
            .pipe(
                skip(1),
                delayWhen(pending => (pending ? of(EMPTY) : timer(1200))),
                untilDestroyed(this)
            )
            .subscribe(pending => {
                this.isPendingDelayedBs$.next(pending);
            });
    }

    ngOnInit(): void {
        // let's reset the relewise retail media params
        this.filterService.resetRelewiseRetailMediaParams();

        // if we are in search result we need to set the relewise params
        this.setRelewiseParams(mapToRelewiseVariation(this.breakpoints()));

        this.filterService.error$
            .pipe(
                filter(err => isDefined<HttpErrorResponse>(err)),
                untilDestroyed(this)
            )
            .subscribe(data => {
                if (data) {
                    this.loggingService.logMessage('product-filter', data.message, data.error);
                }

                this.notificationService.createNotification({
                    type: 'error',
                    translationText: 'filter.notificationMessage.filterError',
                    fadeTime: 10000
                });
            });

        if (this.rootId) {
            this.filterNavigationService.setNavigation(this.rootId);
        }

        if (this.filterPageId) {
            let parsedParams = {};
            if (this.filterService?.filterParamKey) {
                const queryParams = this.route.snapshot.queryParams[this.filterService.filterParamKey];
                if (queryParams) {
                    try {
                        parsedParams = JSON.parse(queryParams);
                    } catch {
                        // we don't need to do anything here
                    }
                }
            }

            const params = {
                take: this.filterService.defaultTakeAmount,
                ...parsedParams
            };

            let retailMediaParams: RelewiseRetailMediaParams | undefined = undefined;
            if (this.filterType() === FilterType.SearchResult) {
                retailMediaParams = {
                    filterType: this.filterType(),
                    retailMediaVariationKey: mapToRelewiseVariation(this.breakpoints()),
                    retailMediaPlacements: 'TOP'
                };
                this.filterService.setRelewiseRetailMediaParams(retailMediaParams);
            }
            const searchParams = buildSearchParams(params, this.filterType());
            this.filterService.initFilterConfiguration(this.filterPageId, searchParams);
            return;
        }

        throw Error(errorMessages.InvalidConfiguration);
    }

    public openMobileFilter(): void {
        this.overlayService.open(MobileFilterComponent);
    }

    public trackProductFn(_: number, product: ProductSearchViewModel): string {
        return product.variantNo;
    }

    public onSameDayDeliveryChange(params: SameDayDeliveryParams): void {
        this.storeStockDisabled = params.sameDayDeliveryIsActive;
        this.cd.markForCheck();
    }

    private setRelewiseParams(variation: RelewiseRetailMediaVariation): void {
        if (this.filterType() === FilterType.SearchResult) {
            this.filterService.setRelewiseRetailMediaParams({
                retailMediaVariationKey: variation,
                retailMediaPlacements: 'TOP'
            });
        }
    }
}
